import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, X, Loader2, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import CommunityTerms from '@/pages/CommunityTerms';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * JoinCommunityModal — Phase 1 Step 4
 *
 * Opens when the user clicks "Join the Community" on their Dashboard
 * invitation card (Step 5). The modal:
 *   1. Shows a handle field with live availability check.
 *   2. Embeds the EXACT Community Terms wording from CommunityTerms.js
 *      via the `embedded` prop — single source of truth.
 *   3. Requires the user to check "I agree" before submitting.
 *   4. POSTs to /api/community/join — on success, the auth user is
 *      refreshed, any retroactive badges are toasted, and the modal
 *      closes.
 *
 * Violet palette matches the Journal page + the standalone /community/terms.
 */
export default function JoinCommunityModal({ open, onClose, onJoined }) {
  const navigate = useNavigate();
  const { setAuthUser } = useAuth();
  const [handle, setHandle] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [anonBenchmarks, setAnonBenchmarks] = useState(true);
  const [check, setCheck] = useState({ status: 'idle', reason: '' });
  const [submitting, setSubmitting] = useState(false);

  // Debounced handle availability check
  useEffect(() => {
    if (!open) return;
    const h = handle.trim();
    if (!h) { setCheck({ status: 'idle', reason: '' }); return; }
    setCheck({ status: 'checking', reason: '' });
    const id = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/community/handle/check`, {
          params: { handle: h },
        });
        if (data.available) {
          setCheck({ status: 'ok', reason: '' });
        } else if (data.valid) {
          setCheck({ status: 'taken', reason: data.reason });
        } else {
          setCheck({ status: 'invalid', reason: data.reason });
        }
      } catch {
        setCheck({ status: 'invalid', reason: 'Could not check handle.' });
      }
    }, 350);
    return () => clearTimeout(id);
  }, [handle, open]);

  // Reset state when closing
  useEffect(() => {
    if (!open) {
      setHandle('');
      setAccepted(false);
      setAnonBenchmarks(true);
      setCheck({ status: 'idle', reason: '' });
      setSubmitting(false);
    }
  }, [open]);

  const canSubmit = check.status === 'ok' && accepted && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/community/join`,
        { handle: handle.trim(), accept_terms: true, anon_benchmarks: anonBenchmarks },
        { withCredentials: true },
      );
      toast.success(`Welcome to the Grove, ${data.handle}!`, {
        description: data.unlocked_badges?.length
          ? `You've already earned ${data.unlocked_badges.length} badge${data.unlocked_badges.length > 1 ? 's' : ''}.`
          : 'Your community profile is live.',
      });
      // Award badges as separate toasts so the user sees them roll in
      (data.unlocked_badges || []).forEach((b, i) => {
        setTimeout(() => {
          toast(`${b.emoji} ${b.label}`, { description: b.blurb });
        }, (i + 1) * 700);
      });
      // Refresh auth user via /auth/me so downstream UI sees community fields
      try {
        const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (setAuthUser) setAuthUser(me.data);
      } catch { /* ignore */ }
      if (onJoined) onJoined(data);
      onClose?.();
      navigate('/community');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not join the community.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="join-community-modal"
    >
      <div className="bg-background rounded-2xl border-2 border-violet-300 dark:border-violet-700 shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-border bg-violet-50/60 dark:bg-violet-950/30 flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-950/60 flex items-center justify-center shrink-0">
            <Users className="w-5 h-5 text-violet-600 dark:text-violet-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-foreground">Join the DiviDrip Community</h2>
            <p className="text-xs text-muted-foreground">Pick a handle, agree to the terms, and you're in.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-1 rounded-md"
            data-testid="join-community-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-5 pb-24 sm:pb-4">
          {/* Handle field */}
          <div>
            <label className="text-sm font-semibold text-foreground block mb-1">
              Community Handle
            </label>
            <p className="text-xs text-muted-foreground mb-2">
              3–20 characters. Letters, numbers, underscore, and dash only.
              This is the name others will see — your real name stays private.
            </p>
            <div className="relative">
              <input
                type="text"
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
                placeholder="e.g. DividendKing88"
                className="w-full h-10 rounded-lg border bg-background px-3 pr-10 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                maxLength={20}
                data-testid="join-handle-input"
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                {check.status === 'checking' && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                {check.status === 'ok' && <Check className="w-4 h-4 text-emerald-500" />}
                {(check.status === 'taken' || check.status === 'invalid') && <X className="w-4 h-4 text-rose-500" />}
              </div>
            </div>
            {check.status === 'ok' && (
              <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1" data-testid="join-handle-ok">
                ✓ Handle is available.
              </p>
            )}
            {(check.status === 'taken' || check.status === 'invalid') && (
              <p className="text-xs text-rose-600 dark:text-rose-400 mt-1" data-testid="join-handle-error">
                {check.reason}
              </p>
            )}
          </div>

          {/* Anonymous benchmark opt-in (default ON) */}
          <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={anonBenchmarks}
                onChange={(e) => setAnonBenchmarks(e.target.checked)}
                className="w-4 h-4 mt-0.5 rounded border-2 border-violet-400 dark:border-violet-600 accent-violet-600 cursor-pointer"
                data-testid="join-anon-benchmarks"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground">Contribute to anonymous community benchmarks</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Your sector weights and yields get pooled with everyone else's to compute averages. <strong>Your identity is stripped before pooling.</strong> No dollar amounts are ever shared.
                </div>
              </div>
            </label>
          </div>

          {/* Terms — embedded */}
          <div className="rounded-lg border border-border bg-background">
            <div className="px-3 py-2 border-b border-border bg-muted/20 text-xs font-semibold text-foreground/80 uppercase tracking-wider">
              Community Terms &amp; Disclaimer
            </div>
            <div className="max-h-72 overflow-y-auto px-3 py-3">
              <CommunityTerms embedded={true} />
            </div>
          </div>

          {/* Agreement checkbox */}
          <label
            className="flex items-start gap-2 cursor-pointer select-none p-3 rounded-lg border-2 border-violet-300 dark:border-violet-700 bg-violet-50/50 dark:bg-violet-950/20"
            data-testid="join-accept-label"
          >
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="w-4 h-4 mt-0.5 rounded border-2 border-violet-400 dark:border-violet-600 accent-violet-600 cursor-pointer"
              data-testid="join-accept-checkbox"
            />
            <span className="text-sm text-foreground font-medium">
              I agree to the DiviDrip Community Terms &amp; Disclaimer.
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 border-t border-border flex items-center justify-end gap-2 bg-muted/20">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="join-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="join-submit-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Joining…</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> Join the Community</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
